import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bars3Icon, BellIcon, DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { relatorioEstagioService } from '@/services/api';
import { RelatorioEstagio } from '@/types';
import { cn } from '@/utils/cn';

interface HeaderProps {
  onMenuClick: () => void;
  isMobile?: boolean;
}

const formatarData = (data: string) => new Date(data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const diasRestantes = (prazo: string) => {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const prazoDate = new Date(prazo);
  prazoDate.setHours(0, 0, 0, 0);
  const diff = Math.ceil((prazoDate.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
  return diff;
};

export const Header: React.FC<HeaderProps> = ({ onMenuClick, isMobile = false }) => {
  const { user, logout } = useAuth();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const userName = user?.nome?.trim() || 'Administrador';
  const userInitial = userName.charAt(0).toUpperCase();

  const { data: proximosVencer = [] } = useQuery<RelatorioEstagio[]>({
    queryKey: ['relatorios-proximos-vencer'],
    queryFn: () => relatorioEstagioService.getProximosVencer(10),
    staleTime: 2 * 60 * 1000, // 2 min
    retry: false,
  });

  useEffect(() => {
    function handleClickOutside(event: globalThis.Event) {
      const target = event.target;
      if (notificationsRef.current && target instanceof globalThis.HTMLElement && !notificationsRef.current.contains(target)) {
        setNotificationsOpen(false);
      }
    }
    if (notificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationsOpen]);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onMenuClick}
          >
            <Bars3Icon className="h-6 w-6" />
          </Button>
          <div className="flex items-center gap-2 lg:ml-0 ml-2">
            <h1 className={cn(
              "font-semibold text-gray-900",
              isMobile ? "text-base" : "text-xl"
            )}>
              {import.meta.env.VITE_APP_TITLE || 'NOME DO SISTEMA'}
            </h1>
          </div>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Notifications - hidden on mobile */}
          {!isMobile && (
            <div className="relative" ref={notificationsRef}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setNotificationsOpen((prev) => !prev)}
                aria-expanded={notificationsOpen}
                aria-haspopup="true"
                className={cn(
                  "relative",
                  notificationsOpen && "bg-primary-100 text-primary-700 ring-2 ring-primary-200 ring-inset"
                )}
              >
                <BellIcon className="h-6 w-6" />
                {proximosVencer.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">
                    {proximosVencer.length > 9 ? '9+' : proximosVencer.length}
                  </span>
                )}
              </Button>
              {notificationsOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg z-50"
                  role="menu"
                >
                  <div className="p-3 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">Notificações</h3>
                    <p className="text-xs text-gray-500">Relatórios com prazo nos próximos 10 dias</p>
                  </div>
                  <div className="py-2">
                    {proximosVencer.length === 0 ? (
                      <div className="px-4 py-8 text-center text-gray-500">
                        <DocumentTextIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">Nenhum relatório próximo do vencimento</p>
                      </div>
                    ) : (
                      proximosVencer.map((r) => {
                        const dias = diasRestantes(r.prazoEntrega);
                        const aluno = r.estagio?.aluno?.nome || 'Aluno';
                        return (
                          <Link
                            key={r.id}
                            to={`/relatorios/${r.id}/editar`}
                            onClick={() => setNotificationsOpen(false)}
                            className="flex gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-l-4 border-l-transparent hover:border-l-amber-400"
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {r.tipo} - {aluno}
                              </p>
                              <p className="text-xs text-gray-600">
                                Prazo: {formatarData(r.prazoEntrega)} • {dias} {dias === 1 ? 'dia' : 'dias'} restante{dias !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </Link>
                        );
                      })
                    )}
                  </div>
                  {proximosVencer.length > 0 && (
                    <div className="p-2 border-t border-gray-100">
                      <Link
                        to="/relatorios"
                        onClick={() => setNotificationsOpen(false)}
                        className="block text-center text-sm text-primary-600 hover:text-primary-700 font-medium py-1"
                      >
                        Ver todos os relatórios
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* User Info */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            {/* User details - hidden on mobile */}
            {!isMobile && (
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{userName}</p>
              </div>
            )}

            {/* Avatar */}
            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-sm font-medium text-primary-700">
                {userInitial}
              </span>
            </div>

            {/* Logout button */}
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className={cn(isMobile && "px-2 text-xs")}
            >
              Sair
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};
